/* eslint-disable no-console */
import languages from "@cospired/i18n-iso-languages";
import enLocale from "@cospired/i18n-iso-languages/langs/en.json";

// Register English language data for language name display
languages.registerLocale(enLocale);

// Custom language mappings for special exceptions
export const customLanguageMappings: Record<string, string> = {
  arb: "Arabic",
  cmn: "Mandarin Chinese",
  el: "Greek",
  fil: "Filipino",
  he: "Hebrew",
  hv: "High Valyrian",
  hw: "Hawaiian",
  km: "Khmer",
  nb: "Norwegian",
  tlh: "Klingon",
  wuu: "Wu Chinese",
  yue: "Cantonese",
  zh: "Mandarin Chinese",
};

/**
 * Converts a language code to its display name in English
 *
 * @param languageCode The ISO language code (2 or 3 letters) or country-specific code (e.g., "en-US")
 * @returns The display name of the language in English, or the original code if not found
 */
export const getDisplayLanguageName = (languageCode: string): string => {
  try {
    if (!languageCode || typeof languageCode !== "string") {
      return "N/A";
    }

    // Check direct mapping first (for full codes like "en-US")
    if (customLanguageMappings[languageCode]) {
      return customLanguageMappings[languageCode];
    }

    // Extract the language part before the dash (if any)
    const baseLangCode = languageCode.split("-")[0];

    // Check if we have a custom mapping for this base language code
    if (customLanguageMappings[baseLangCode]) {
      return customLanguageMappings[baseLangCode];
    }

    // Try to get the English name for the language code
    if (baseLangCode.length === 2) {
      // Try as alpha-2 code (like 'en')
      try {
        const name = languages.getName(baseLangCode, "en");
        if (name) {
          return name;
        }
      } catch (e) {
        // Ignore errors with language lookup
      }
    } else if (baseLangCode.length === 3) {
      // Try as alpha-3 code (like 'eng')
      try {
        const name = languages.getName(baseLangCode, "en");
        if (name) {
          return name;
        }
      } catch (e) {
        // Ignore errors with language lookup
      }
    }

    // If we couldn't find a mapping, return the original code
    return languageCode;
  } catch (error) {
    // If there's an error parsing the code, just use the original code
    console.warn(`Error parsing language code: ${languageCode}`, error);
    return typeof languageCode === "string" ? languageCode : "N/A";
  }
};

/**
 * Formats display text by converting standalone language codes to their display names
 *
 * @param text The text potentially containing language codes
 * @returns The text with language codes replaced by their display names
 */
export const formatDisplayText = (text: string): string =>
  // This regex matches 2 or 3 letter language codes that stand alone
  // It won't match codes that are part of longer words
  text.replace(/\b([a-z]{2,3})\b/gi, match => {
    const displayName = getDisplayLanguageName(match);
    // Only replace if we got a different name (don't replace codes that didn't match)
    return displayName === match ? match : displayName;
  });
